import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loginUser, registerUser, logoutUser, getAccessToken, refreshAccessToken } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const token = getAccessToken();
      if (token) {
        try {
          // Decode JWT to get user info (basic decode, no verification needed here)
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUser({
            id: payload.sub,
            email: payload.email,
            name: payload.name,
          });
          setIsAuthenticated(true);
        } catch (error) {
          // Invalid token, try to refresh
          try {
            const data = await refreshAccessToken();
            const payload = JSON.parse(atob(data.accessToken.split('.')[1]));
            setUser({
              id: payload.sub,
              email: payload.email,
              name: payload.name,
            });
            setIsAuthenticated(true);
          } catch {
            // Refresh failed, stay as guest
            logoutUser();
          }
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (credentials) => {
    try {
      const data = await loginUser(credentials);
      const payload = JSON.parse(atob(data.accessToken.split('.')[1]));
      setUser({
        id: payload.sub,
        email: payload.email,
        name: payload.name,
      });
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const data = await registerUser(userData);
      const payload = JSON.parse(atob(data.accessToken.split('.')[1]));
      setUser({
        id: payload.sub,
        email: payload.email,
        name: payload.name,
      });
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const logout = useCallback(() => {
    logoutUser();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
